import { useState, useEffect } from 'react'
import { SteppedModalProps } from './SteppedModal'
import { ValidationSummary } from './ValidationSummary'

interface ComponentProps extends Record<string, unknown> {
  onSubmit: (...args: unknown[]) => Promise<void>
}

export type ValidationItem = {
  component: React.ComponentType<any>
  componentProps: ComponentProps
  message: string
  /**
   * Optional explicit next-step key. Omit it to chain by assembled order
   * (the default) — required for composing more than one validation source.
   */
  nextStep?: string
  stepKey: string
  stepName: string
}

type StepAction = {
  canSkip?: boolean
  text: string
  onAction: () => Promise<void>
}

export type Step = {
  component: React.ComponentType<any>
  componentProps: Record<string, any>
  key: string
  showBreadcrumb: boolean
  wrapper: 'MODAL' | 'DIALOG'
  actions?: {
    close?: StepAction
    cancel: StepAction
    confirm: StepAction
  }
  name?: string
  modalTitle?: string
}

const convertValidationItemsToSteps = (
  validation: ValidationItem[],
  goToStep: (step: string) => void
): Step[] => {
  return validation.map(
    ({ stepName, component, componentProps, stepKey, nextStep }, index) => {
      // Advance to the explicit nextStep when given, else chain by assembled
      // order (each step -> the next, last -> CONFIRM). The order fallback is
      // what lets more than one validation source compose; a per-source
      // hardcoded nextStep would skip every step after the first.
      const nextStepKey =
        nextStep ?? validation[index + 1]?.stepKey ?? 'CONFIRM'

      return {
        component,
        componentProps: {
          ...componentProps,
          onSubmit: async (...args: unknown[]) => {
            await componentProps.onSubmit(...args)
            goToStep(nextStepKey)
          }
        },
        key: stepKey,
        name: stepName,
        showBreadcrumb: true,
        wrapper: 'MODAL'
      }
    }
  )
}

export const useSteppedModal = (props: SteppedModalProps) => {
  const { onClose, onFinalConfirm, canSkip } = props
  const [activeStepName, setActiveStepName] = useState<string>('')
  const goToStep = (stepKey: string) => setActiveStepName(stepKey)

  const summaryStep: Step = {
    actions: {
      close: {
        text: '',
        onAction: () => Promise.resolve(onClose())
      },
      cancel: {
        canSkip,
        text: 'Skip for now',
        onAction: () => {
          goToStep('CONFIRM')
          return Promise.resolve()
        }
      },
      confirm: {
        text: 'Resolve issues',
        onAction: () => Promise.resolve()
      }
    },
    component: ValidationSummary,
    componentProps: {
      validation: props.validation,
      validationSummaryText: props.validationSummaryText
    },
    key: 'SUMMARY',
    modalTitle: 'Before you continue',
    showBreadcrumb: false,
    wrapper: 'DIALOG'
  }

  const confirmationStep: Step = {
    actions: {
      cancel: {
        canSkip: true,
        text: 'Cancel',
        onAction: () => Promise.resolve(onClose())
      },
      confirm: {
        text: props.confirmActionText,
        onAction: onFinalConfirm
      }
    },
    component: () => props.confirmationSummaryText,
    componentProps: {},
    key: 'CONFIRM',
    modalTitle: props.confirmTitleText,
    showBreadcrumb: false,
    wrapper: 'DIALOG'
  }

  const validationSteps: Array<Step> = convertValidationItemsToSteps(
    props.validation,
    goToStep
  )

  useEffect(() => {
    if (props.validation.length === 0) {
      setActiveStepName(confirmationStep.key)
      return
    }

    setActiveStepName(summaryStep.key)
  }, [])

  const steps =
    props.validation.length === 0
      ? [confirmationStep]
      : [summaryStep, ...validationSteps, confirmationStep]

  /**
   * Steps are looked up by key rather than by reference: the `steps` array is
   * recreated on every render, so a step object captured in an earlier render
   * would never be found by an identity check.
   */
  const activeStep = steps.find(({ key }) => key === activeStepName)

  const transitionToNextStep = () => {
    const activeStepIndex = steps.findIndex(({ key }) => key === activeStepName)
    const isLastStep = activeStepIndex === steps.length - 1

    if (activeStepIndex === -1 || isLastStep) {
      return
    }

    setActiveStepName(steps[activeStepIndex + 1].key)
  }

  return { activeStep, transitionToNextStep }
}
